import React from "react";
import type {
  Line,
  LineDirection,
} from "../editLineCrossingsStore/editLineCrossingsStore";

interface LineOverlayProps {
  lines: Line[];
  width: number;
  height: number;
}

// The LineDrawer uses 20px padding, so coordinates are stored with that offset
const PADDING_OFFSET = 20;

// Calculate arrow path for a given direction
const getArrowPath = (
  centerX: number,
  centerY: number,
  perpDx: number,
  perpDy: number,
  sideMultiplier: number,
  fromCenter: boolean
): { linePath: string; arrowPath: string } => {
  const crossingLineLength = 50;
  const halfLength = crossingLineLength / 2;
  const arrowLength = 12;
  const arrowWidth = (2 * arrowLength) / Math.sqrt(3);

  // Calculate the perpendicular offset
  const offsetX = perpDx * halfLength * sideMultiplier;
  const offsetY = perpDy * halfLength * sideMultiplier;

  // Calculate the arrow tip position
  const arrowTipX = centerX + offsetX;
  const arrowTipY = centerY + offsetY;

  // Calculate where the line should end (arrowhead base)
  const arrowBaseOffsetX = perpDx * arrowLength * sideMultiplier;
  const arrowBaseOffsetY = perpDy * arrowLength * sideMultiplier;
  const lineEndX = arrowTipX - arrowBaseOffsetX;
  const lineEndY = arrowTipY - arrowBaseOffsetY;

  // Start point of the crossing line
  const crossStartX = fromCenter ? centerX : centerX - offsetX;
  const crossStartY = fromCenter ? centerY : centerY - offsetY;

  // Line path
  const linePath = `M ${crossStartX} ${crossStartY} L ${lineEndX} ${lineEndY}`;

  // Arrow head path (triangle)
  // Calculate the two base corners of the arrow
  const arrowAngle = Math.atan2(offsetY, offsetX);
  const cos = Math.cos(arrowAngle);
  const sin = Math.sin(arrowAngle);

  // Arrow base corners (perpendicular to arrow direction)
  const baseCorner1X = arrowTipX - arrowLength * cos + (arrowWidth / 2) * sin;
  const baseCorner1Y = arrowTipY - arrowLength * sin - (arrowWidth / 2) * cos;
  const baseCorner2X = arrowTipX - arrowLength * cos - (arrowWidth / 2) * sin;
  const baseCorner2Y = arrowTipY - arrowLength * sin + (arrowWidth / 2) * cos;

  const arrowPath = `M ${arrowTipX} ${arrowTipY} L ${baseCorner1X} ${baseCorner1Y} L ${baseCorner2X} ${baseCorner2Y} Z`;

  return { linePath, arrowPath };
};

const renderDirectionArrow = (line: Line, index: number): React.ReactNode[] => {
  // Adjust coordinates by subtracting the padding offset
  const start: [number, number] = [
    line.coordinates[0][0] - PADDING_OFFSET,
    line.coordinates[0][1] - PADDING_OFFSET,
  ];
  const end: [number, number] = [
    line.coordinates[1][0] - PADDING_OFFSET,
    line.coordinates[1][1] - PADDING_OFFSET,
  ];

  const centerX = (start[0] + end[0]) / 2;
  const centerY = (start[1] + end[1]) / 2;

  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lineLength = Math.sqrt(dx * dx + dy * dy);

  if (lineLength === 0) return [];

  const perpDx = -dy / lineLength;
  const perpDy = dx / lineLength;

  const arrowColor = "#FF1744"; // Neon red
  const elements: React.ReactNode[] = [];

  if (line.direction === "AtoB") {
    const { linePath, arrowPath } = getArrowPath(
      centerX,
      centerY,
      perpDx,
      perpDy,
      1,
      false
    );
    elements.push(
      <path
        key={`line-${index}-arrow-line`}
        d={linePath}
        stroke={arrowColor}
        strokeWidth={2}
        fill="none"
      />,
      <path key={`line-${index}-arrow-head`} d={arrowPath} fill={arrowColor} />
    );
  } else if (line.direction === "BtoA") {
    const { linePath, arrowPath } = getArrowPath(
      centerX,
      centerY,
      perpDx,
      perpDy,
      -1,
      false
    );
    elements.push(
      <path
        key={`line-${index}-arrow-line`}
        d={linePath}
        stroke={arrowColor}
        strokeWidth={2}
        fill="none"
      />,
      <path key={`line-${index}-arrow-head`} d={arrowPath} fill={arrowColor} />
    );
  } else if (line.direction === "Both") {
    const arrow1 = getArrowPath(centerX, centerY, perpDx, perpDy, -1, true);
    const arrow2 = getArrowPath(centerX, centerY, perpDx, perpDy, 1, true);
    elements.push(
      <path
        key={`line-${index}-arrow-line-1`}
        d={arrow1.linePath}
        stroke={arrowColor}
        strokeWidth={2}
        fill="none"
      />,
      <path
        key={`line-${index}-arrow-head-1`}
        d={arrow1.arrowPath}
        fill={arrowColor}
      />,
      <path
        key={`line-${index}-arrow-line-2`}
        d={arrow2.linePath}
        stroke={arrowColor}
        strokeWidth={2}
        fill="none"
      />,
      <path
        key={`line-${index}-arrow-head-2`}
        d={arrow2.arrowPath}
        fill={arrowColor}
      />
    );
  }

  return elements;
};

export const LineOverlay: React.FC<LineOverlayProps> = ({
  lines,
  width,
  height,
}) => {
  return (
    <svg
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
      }}
    >
      {lines.map((line, index) => {
        // Adjust coordinates by subtracting the padding offset
        const x1 = line.coordinates[0][0] - PADDING_OFFSET;
        const y1 = line.coordinates[0][1] - PADDING_OFFSET;
        const x2 = line.coordinates[1][0] - PADDING_OFFSET;
        const y2 = line.coordinates[1][1] - PADDING_OFFSET;

        return (
          <React.Fragment key={`line-group-${index}`}>
            {/* Main line */}
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={line.color}
              strokeWidth={2}
            />
            {/* Direction arrow(s) */}
            {renderDirectionArrow(line, index)}
          </React.Fragment>
        );
      })}
    </svg>
  );
};
